// Pure piano keyboard geometry: range normalization (whole octaves C…B, at
// least two octaves, clamped to the 88 keys A0–C8), key rectangles and
// pointer hit-testing (black keys first).

export const PIANO_LOW = 21; // A0
export const PIANO_HIGH = 108; // C8
export const MIDDLE_C = 60;
/** Default range: C3–B5. */
export const DEFAULT_PIANO_RANGE: readonly [number, number] = [48, 83];

const BLACK_PCS = new Set([1, 3, 6, 8, 10]);

export function isBlackKey(midi: number): boolean {
  return BLACK_PCS.has(((midi % 12) + 12) % 12);
}

/**
 * Expand a range to whole octaves (C…B) spanning at least `minSemis`
 * semitones, clamped to the piano (A0…C8).
 */
export function normalizePianoRange(lo: number, hi: number, minSemis = 24): [number, number] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [DEFAULT_PIANO_RANGE[0], DEFAULT_PIANO_RANGE[1]];
  let a = Math.max(PIANO_LOW, Math.min(PIANO_HIGH, Math.round(Math.min(lo, hi))));
  let b = Math.max(PIANO_LOW, Math.min(PIANO_HIGH, Math.round(Math.max(lo, hi))));
  a -= ((a % 12) + 12) % 12; // down to C
  b += 11 - (((b % 12) + 12) % 12); // up to B
  const span = () => Math.min(b, PIANO_HIGH) - Math.max(a, PIANO_LOW) + 1;
  while (span() < minSemis) {
    if (b + 1 <= PIANO_HIGH) b += 12;
    else if (a > PIANO_LOW) a -= 12;
    else break;
  }
  return [Math.max(a, PIANO_LOW), Math.min(b, PIANO_HIGH)];
}

export interface PianoKey {
  midi: number;
  black: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PianoLayout {
  lo: number;
  hi: number;
  width: number;
  height: number;
  whiteW: number;
  blackW: number;
  blackH: number;
  whites: PianoKey[];
  blacks: PianoKey[];
  /** midi → key */
  byMidi: Map<number, PianoKey>;
}

/** Black key centre offset from the boundary between its neighbours, in white-key widths. */
const BLACK_OFFSET: Record<number, number> = { 1: -0.1, 3: 0.1, 6: -0.13, 8: 0, 10: 0.13 };

export function whiteKeyCount(lo: number, hi: number): number {
  let n = 0;
  for (let m = lo; m <= hi; m++) if (!isBlackKey(m)) n++;
  return n;
}

/** Key rectangles for [lo, hi] (both should be white keys) in a width × height box. */
export function pianoLayout(lo: number, hi: number, width: number, height: number, top = 0): PianoLayout {
  if (isBlackKey(lo)) lo--;
  if (isBlackKey(hi)) hi++;
  const nWhite = Math.max(1, whiteKeyCount(lo, hi));
  const whiteW = width / nWhite;
  const blackW = whiteW * 0.58;
  const keyH = height - top;
  const blackH = keyH * 0.62;
  const whites: PianoKey[] = [];
  const blacks: PianoKey[] = [];
  const byMidi = new Map<number, PianoKey>();
  let wi = 0;
  for (let m = lo; m <= hi; m++) {
    if (!isBlackKey(m)) {
      const k: PianoKey = { midi: m, black: false, x: wi * whiteW, y: top, w: whiteW, h: keyH };
      whites.push(k);
      byMidi.set(m, k);
      wi++;
    } else {
      const boundary = wi * whiteW;
      const cx = boundary + BLACK_OFFSET[((m % 12) + 12) % 12] * whiteW;
      const k: PianoKey = { midi: m, black: true, x: cx - blackW / 2, y: top, w: blackW, h: blackH };
      blacks.push(k);
      byMidi.set(m, k);
    }
  }
  return { lo, hi, width, height, whiteW, blackW, blackH, whites, blacks, byMidi };
}

/** MIDI key under a point (black keys take precedence), or null. */
export function hitTestPiano(l: PianoLayout, x: number, y: number): number | null {
  if (x < 0 || x >= l.width || y < (l.whites[0]?.y ?? 0) || y > l.height) return null;
  for (const k of l.blacks) if (x >= k.x && x < k.x + k.w && y >= k.y && y < k.y + k.h) return k.midi;
  const i = Math.floor(x / l.whiteW);
  return l.whites[Math.max(0, Math.min(l.whites.length - 1, i))]?.midi ?? null;
}
